import { ICloneable } from '../../../contracts/cloneable.js';
import { IDataComparable } from '../../../contracts/data-comparable.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { isSameData, SerializationContext, SerializedData } from '../../dto/dto.js';
import { Currency } from '../currency.js';
import { EventTicketOrder } from '../event-ticket-order.js';
import { OrderSource } from '../order-source.js';
import { TicketType } from '../ticket-type.js';
import { OrderDiscountDetails } from '../../../utility/statistics_discount_utils.js';
import { OnlineEventTicketOrder } from '../online-event-ticket-order.js';
import { SalesIncomeSummary } from './sales-income-summary.js';

export class TicketTemplateSalesIncomeSummary implements IDataComparable, ICloneable {
  constructor(
    public readonly templateId: string,
    public readonly sales: SalesIncomeSummary,
  ) {}

  static empty(currency: Currency, templateId: string): TicketTemplateSalesIncomeSummary {
    return new TicketTemplateSalesIncomeSummary(
        templateId,
        SalesIncomeSummary.empty(currency),
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): TicketTemplateSalesIncomeSummary {
    return new TicketTemplateSalesIncomeSummary(
        MapUtils.get<string>(map, 'templateId'),
        SalesIncomeSummary.fromMap(context, MapUtils.get<SerializedData>(map, 'sales')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'templateId': this.templateId,
      'sales': this.sales.toMap(context),
    };
  }

  clone(): this {
    return new TicketTemplateSalesIncomeSummary(
        this.templateId,
        this.sales.clone(),
    ) as this;
  }

  isSame(other: TicketTemplateSalesIncomeSummary): boolean {
    if(other === this) {
      return true;
    }

    return other.templateId === this.templateId && isSameData(other.sales, this.sales);
  }

  addEventTicketOfTicketOrder(order: EventTicketOrder, discountDetails: OrderDiscountDetails | null, index: number) {
    const isOnline = order.source === OrderSource.Client;
    const isPromoter = order.tickets[index].ticketType === TicketType.Promoter;
    const isProtocol = order.tickets[index].ticketType === TicketType.Protocol;
    const price = discountDetails?.ticketsDetails?.get(order.tickets[index].uniqueId)?.discountedPrice ?? order.tickets[index].template.price.clone();

    if(isPromoter) {
      this.sales.promoterTickets++;
      this.sales.promoterIncome = this.sales.promoterIncome.add(price);
    }
    else if(isProtocol) {
      this.sales.protocolTickets++;
      this.sales.protocolIncome = this.sales.protocolIncome.add(price);
    }
    else if(isOnline) {
      this.sales.onlineTickets++;
      this.sales.onlineIncome = this.sales.onlineIncome.add(price);
    }
    else {
      this.sales.offlineTickets++;
      this.sales.offlineIncome = this.sales.offlineIncome.add(price);
    }

    this.sales.totalTickets++;
    this.sales.totalIncome = this.sales.totalIncome.add(price);
  }

  addEventTicketOfOnlineTicketOrder(order: OnlineEventTicketOrder, discountDetails: OrderDiscountDetails | null, ticketIndex: number) {
    const price = discountDetails?.ticketsDetails?.get(order.tickets[ticketIndex].uniqueId)?.discountedPrice ?? order.tickets[ticketIndex].template.price.clone();

    this.sales.onlineTickets++;
    this.sales.onlineIncome = this.sales.onlineIncome.add(price);

    this.sales.totalTickets++;
    this.sales.totalIncome = this.sales.totalIncome.add(price);
  }

  removeEventTicketOfTicketOrder(order: EventTicketOrder, discountDetails: OrderDiscountDetails | null, index: number) {
    const isOnline = order.source === OrderSource.Client;
    const isPromoter = order.tickets[index].ticketType === TicketType.Promoter;
    const isProtocol = order.tickets[index].ticketType === TicketType.Protocol;
    const price = discountDetails?.ticketsDetails?.get(order.tickets[index].uniqueId)?.discountedPrice ?? order.tickets[index].template.price.clone();

    if(isPromoter) {
      this.sales.promoterTickets--;
      this.sales.promoterIncome = this.sales.promoterIncome.difference(price);
    }
    else if(isProtocol) {
      this.sales.protocolTickets--;
      this.sales.protocolIncome = this.sales.protocolIncome.difference(price);
    }
    else if(isOnline) {
      this.sales.onlineTickets--;
      this.sales.onlineIncome = this.sales.onlineIncome.difference(price);
    }
    else {
      this.sales.offlineTickets--;
      this.sales.offlineIncome = this.sales.offlineIncome.difference(price);
    }

    this.sales.totalTickets--;
    this.sales.totalIncome = this.sales.totalIncome.difference(price);

    this.sales.ensurePositive();
  }

  removeEventTicketOfOnlineTicketOrder(order: OnlineEventTicketOrder, discountDetails: OrderDiscountDetails | null, ticketIndex: number) {
    const price = discountDetails?.ticketsDetails?.get(order.tickets[ticketIndex].uniqueId)?.discountedPrice ?? order.tickets[ticketIndex].template.price.clone();

    this.sales.onlineTickets--;
    this.sales.onlineIncome = this.sales.onlineIncome.difference(price);

    this.sales.totalTickets--;
    this.sales.totalIncome = this.sales.totalIncome.difference(price);

    this.sales.ensurePositive();
  }
}
